////////////////////////////// main-code //////////////////////////////////////

import React, { useEffect, useState } from "react";
import { ChevronRight, RotateCcw, ArrowLeft, User, RefreshCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getCertVendorCapabilityData } from "../../../services/apiService";
import { decodedToken } from '../../../utils/auth';
import Swal from 'sweetalert2';

export default function MNTCertificationCapability() {

  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [certItems, setCertItems] = useState([]);
  const [logoGroups, setLogoGroups] = useState({});
  const [selectedODMs, setSelectedODMs] = useState([]);
  const [selectedLogos, setSelectedLogos] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [username, setUsername] = useState('');
  const maxODMs = 2;

  useEffect(() => {
    const user = decodedToken();
    console.log("Decoded user:", user);

    if (user) {
      setUsername(
        user.userId ||
        user.name ||
        user.email ||
        user.username ||
        user.sub ||
        "Unknown"
      );
    }
  }, []);
  // API CALL
 useEffect(() => {
  const fetchData = async () => {
    try {
      const res = await getCertVendorCapabilityData({
        productType: "MNT"
      });

      // Handle "Template Missing" response
      if (
        res?.success === false &&
        res?.error === "Template not found"
      ) {
        await Swal.fire({
          icon: "warning",
          title: "Template Missing",
          text: "File needs to be uploaded in Template Management",
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb",
          background: "white",
          color: "black"
        });

        // Navigate to Vendor Capability -> Certification
        navigate("/vendor-capability", {
          state: { view: "certification" }
        });

        return;
      }

      const data = res?.data || {};

      setVendors(data?.vendors || []);
      setCertItems(data?.certItems || []);

      // Build logo groups dynamically
      const groups = {};

      (data?.certItems || []).forEach((item) => {
        if (!groups[item.functionCategory]) {
          groups[item.functionCategory] = [];
        }
        groups[item.functionCategory].push(item);
      });

      setLogoGroups(groups);
    } catch (err) {
      console.error("API Error:", err);

      // Handle 404 if backend still returns 404
      if (err.response?.status === 404) {
        await Swal.fire({
          icon: "warning",
          title: "Template Missing",
          text: "File needs to be uploaded in Template Management",
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb",
          background: "white",
          color: "black"
        });

        navigate("/vendor-capability", { state: { view: "certification" }
        });
        return;
      }

      // Handle other API/server errors
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Something went wrong",
        confirmButtonColor: "#dc2626",
        background: "white",
        color: "black"
      });
    }
  };

  fetchData();
}, [navigate]);

  // Toggle ODM
  const toggleODM = (vendor) => {
    setSelectedODMs(prev => {
      if (prev.includes(vendor))
        return prev.filter(v => v !== vendor);

      if (prev.length >= maxODMs)
        return prev;

      return [...prev, vendor];
    });
  };

  // Toggle Logo
  const toggleLogo = (logo) => {
    setSelectedLogos(prev =>
      prev.includes(logo)
        ? prev.filter(v => v !== logo)
        : [...prev, logo]
    );
  };

  const reset = () => {
    setSelectedODMs([]);
    setSelectedLogos([]);
    setShowResults(false);
  };

  const handleConfirm = () => {
    if (!selectedODMs.length) {
      alert("Please select ODM vendor");
      return;
    }
    if (!selectedLogos.length) {
      alert("Please select logo");
      return;
    }
    setShowResults(true);
  };

  const filteredItems = certItems.filter(item =>
    selectedLogos.includes(item.certificationItem)
  );

  // ==================== RESULT TABLE SCREEN ====================
  const allowedKeys = [
    "membership",
    "preTestAbility",
    "debugAbility",
    "thirdPartyLab"
  ];

  const subHeaderKeys = Object.keys(vendors[0]?.subHeaders || {})
    .filter(key => allowedKeys.includes(key));

  const isConfirmDisabled = selectedODMs.length === 0 || selectedLogos.length === 0;
  const isSingleODM = selectedODMs.length === 1;
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-gray-700 bg-black px-3 sm:px-4 md:px-6 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0 overflow-hidden">
            <ArrowLeft
              className="cursor-pointer w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 shrink-0"
              onClick={() => {
                if (showResults) {
                  setShowResults(false);
                } else {
                  navigate("/vendor-capability", { state: { view: "certification" } });
                }
              }}
            />
            <h1 className="font-bold text-base sm:text-lg md:text-xl lg:text-3xl truncate sm:truncate">
              MNT Certification Capability
            </h1>
          </div>
          <div className="flex items-center sm:items-center gap-2 mt-2 sm:mt-0 sm:ml-2">
            <User className="w-4 h-4 sm:w-5 sm:h-5 text-white shrink-0" />
            <span className="text-xs sm:text-sm md:text-base font-medium text-white break-words">
              {username || "Current User"}
            </span>
          </div>
        </div>
      </header>

      {showResults ? (
        <>
          {/* RESULT TITLE */}
          <div className="flex justify-center mt-3">
            <div className="flex items-center gap-5 sm:gap-6 md:gap-12">
              <div className="w-10 sm:w-15 md:w-25 h-[3px] bg-white "></div>
              <span className="text-sm sm:text-lg md:text-xl font-semibold whitespace-nowrap">
                Filter Result
              </span>
              <div className="w-10 sm:w-14 md:w-25 h-[3px] bg-white"></div>
            </div>
          </div>
          <div className="relative mt-2 px-4 sm:px-6">
            <div className="flex justify-end mb-2 pr-2">
              <div className="text-yellow-400 text-sm font-medium">
                Y : Yes, capable
              </div>
            </div>
            <div className="overflow-x-auto overflow-y-auto sm:max-h-[calc(100vh-180px)] max-h-[calc(100vh-150px)] border-b border-gray-500 bg-black">
             <table className="table-auto border-separate border-spacing-0 min-w-[650px] w-full text-xs sm:text-sm border border-gray-700">

                <thead className="sticky top-0 z-30 bg-[#345c8c] text-white text-[10px] sm:text-xs md:text-sm shadow-md text-center">
                  <tr>
                    <th
                      rowSpan={2}
                      className="border px-1 py-1 sm:px-2 sm:py-1 w-[80px] sm:w-[60px] text-center"
                    >Certification items</th>

                    <th
                      rowSpan={2}
                      className="border px-1 py-1 sm:px-2 sm:py-1 w-[90px] sm:w-[170px] text-center"
                    >  Certified logo</th>
                    {subHeaderKeys.map(key => (
                    <th
                      key={key}
                      colSpan={isSingleODM ? 1 : selectedODMs.length}
                      className="border px-2 py-1 sm:px-3 sm:py-2 text-center whitespace-nowrap"
                    >
                      {vendors[0]?.subHeaders?.[key]?.en || key}
                    </th>
                  ))}
                  </tr>

                 <tr className="bg-pink-500 text-black text-[9px] sm:text-[11px] md:text-xs text-center">
                    {isSingleODM ? (
                      <th
                        colSpan={subHeaderKeys.length}
                        className=" px-1 sm:px-2 py-1 whitespace-nowrap text-center border border-white"
                      >
                        {selectedODMs[0]}
                      </th>
                    ) : (
                      subHeaderKeys.map(key =>
                        selectedODMs.map(vendor => (
                          <th
                            key={key + vendor}
                            className=" px-1 sm:px-2 py-1 whitespace-nowrap text-center border border-white"
                          >
                            {vendor}
                          </th>
                        ))
                      )
                    )}
                  </tr>
                </thead>

                <tbody>
                  {filteredItems.map((item, i) => (
                    <tr key={i} className="border border-gray-800 hover:bg-gray-900 text-center">

                      <td className="border px-2 py-1 sm:px-3 sm:py-2 bg-blue-950 whitespace-nowrap text-center">
                        {item.certificationItem}
                      </td>

                      <td className="border px-2 py-1 sm:px-3 sm:py-2 bg-gray-900 text-center">
                        {item.logoImage && (
                          <img
                            src={`data:image/png;base64,${item.logoImage.base64}`}
                            alt="logo"
                            className="h-6 sm:h-12 text-center"
                          />
                        )}
                      </td>

                     {subHeaderKeys.map(key =>
                      (isSingleODM
                        ? [selectedODMs[0]]
                        : selectedODMs
                      ).map(vendor => {
                          const value = item.vendorData?.[vendor]?.[key];

                          return (
                            <td
                              key={key + vendor}
                              className={`border text-center py-3 ${value === "Y" ? "bg-black text-white" : "bg-black"
                                }`}
                            >
                              {value === "Y" ? "Y" : ""}
                            </td>
                          );
                        })
                      )}

                    </tr>
                  ))}

                  {filteredItems.length === 0 && (
                    <tr>
                      <td colSpan={2 + selectedODMs.length * 4} className="py-12 text-center text-gray-500">
                        No matching certification items found
                      </td>
                    </tr>
                  )}
                </tbody>

              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* FILTER SCREEN (UNCHANGED) */}
          <main className="max-w-8xl mx-auto px-6 py-6">

            {/* Instruction */}

            <div className="bg-blue-950 p-3 mb-2 -mt-3">
              <p className="pl-2 sm:pl-3 md:pl-15 text-xs sm:text-sm md:text-base lg:text-lg">
                Please make a selection first to view the comparison results.
              </p>

              <p className="mt-2 pl-2 sm:pl-3 md:pl-15 text-xs sm:text-sm md:text-base lg:text-lg">
                Step 1 : Select ODM vendor
              </p>

              <p className="pl-2 sm:pl-3 md:pl-15 text-xs sm:text-sm md:text-base lg:text-lg">
                Step 2 : Select the logo you'd like to view
              </p>
            </div>
            <section className="border border-gray-700 rounded-xl p-6 mb-2">
              <div className="relative flex flex-col sm:flex-row items-center mb-2">
                <h2 className="text-base sm:text-sm md:text-base lg:text-lg font-bold text-center sm:mx-auto sm:-mt-4">
                  1.0 Select ODM
                </h2>
                <span className="text-yellow-400 text-base sm:text-sm md:text-base lg:text-lgmt-1 sm:mt-0 sm:absolute sm:right-0 sm:-mt-4 text-center sm:text-right">
                  *Select at most 2 options
                </span>

              </div>

              {/* ODM OPTIONS */}
              <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 lg:gap-10 xl:gap-35 lg:ml-[180px]">
                {vendors.map(vendor => {
                  const name = vendor.name;
                  const selected = selectedODMs.includes(name);
                  const disabled = !selected && selectedODMs.length >= maxODMs;

                  return (
                    <label
                      key={name}
                      className="flex items-center gap-2 text-sm sm:text-base md:text-lg lg:text-xl cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={disabled}
                        onChange={() => toggleODM(name)}
                        className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 accent-cyan-500"
                      />
                      {name}
                    </label>
                  );
                })}
              </div>

            </section>

            {/* LOGO SECTION */}
            <section className="border border-gray-700 rounded-xl p-6">
              <div className="relative flex flex-col sm:flex-row items-center">

                <h2 className="text-base sm:text-sm md:text-base lg:text-lg font-bold text-center sm:mx-auto sm:-mt-4">
                  2.0 Select Logo
                </h2>

                <span className="text-yellow-400 text-base sm:text-sm md:text-base lg:text-lg mt-1 sm:mt-0 sm:absolute sm:right-0 sm:-mt-4 text-center sm:text-right">
                  *Select at least one option
                </span>

              </div>

              {Object.keys(logoGroups).map(group => (
                <div key={group} className="mb-5">
                  <h3 className="font-semibold mb-1 text-base sm:text-lg md:text-xl lg:text-xl">
                    [{group}]
                  </h3>

                  <div className="flex flex-wrap gap-4 sm:gap-6">
                    {logoGroups[group].map(item => {
                      const logo = item.certificationItem;
                      const selected = selectedLogos.includes(logo);

                      return (
                        <label
                          key={logo}
                          className="flex items-center gap-2 cursor-pointer text-sm sm:text-base md:text-lg lg:text-xl"
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleLogo(logo)}
                            className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 accent-cyan-500"
                          />
                          {logo}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

            </section>

            {/* Buttons */}
            <div className="flex justify-center gap-10 mt-2">
              <button onClick={reset} className="flex items-center gap-2 bg-gray-800 px-6 py-3 rounded">

                Reset
                <RefreshCcw size={20} />
              </button>
              <button
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
                className={`flex items-center gap-2 px-6 py-3 rounded transition-all
                 ${isConfirmDisabled
                    ? "bg-gray-600 cursor-not-allowed opacity-60"
                    : "bg-blue-600 hover:bg-blue-700"}`}
              >
                Confirm
                <ChevronRight size={20} />
              </button>
            </div>
          </main>
        </>
      )}
    </div>
  );
}